import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Tags, Network, BookOpen, Sparkles, Upload, ExternalLink, Loader2 } from 'lucide-react'
import { useTagsets, type Tagset } from '@/hooks/useTagsets'

interface Step4AdvancedProps {
  selectedTagsetIds?: string[]
  onTagsetSelectionChange?: (ids: string[]) => void
}

const sourceIcon = (source: Tagset['source_type']) => {
  switch (source) {
    case 'genclair':
      return <Sparkles className="h-3.5 w-3.5 text-purple-500" />
    case 'upload':
      return <Upload className="h-3.5 w-3.5 text-blue-500" />
    default:
      return <Tags className="h-3.5 w-3.5 text-muted-foreground" />
  }
}

export function Step4Advanced({ selectedTagsetIds = [], onTagsetSelectionChange }: Step4AdvancedProps) {
  const { data, isLoading } = useTagsets({ page: 1, pageSize: 100, search: '' })
  const readyTagsets = data?.items?.filter((t: Tagset) => t.status === 'DONE') || []

  const toggleTagset = (id: string) => {
    if (!onTagsetSelectionChange) return
    const next = selectedTagsetIds.includes(id)
      ? selectedTagsetIds.filter((tid) => tid !== id)
      : [...selectedTagsetIds, id]
    onTagsetSelectionChange(next)
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-1">Advanced Features</h3>
        <p className="text-sm text-muted-foreground">
          Link tagsets and enable advanced retrieval features for this knowledge base.
        </p>
      </div>

      {/* Tagset Selector */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Tags className="h-5 w-5 text-primary" />
              <CardTitle className="text-base">Tagset Configuration</CardTitle>
            </div>
            <Link to="/app/tagsets" className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1">
              <ExternalLink className="h-3 w-3" />
              Manage Tagsets
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Select tagsets to link after creation. Tags improve retrieval by categorizing chunks during parsing.
          </p>

          {isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : readyTagsets.length > 0 ? (
            <div className="space-y-2">
              {readyTagsets.map((tagset) => (
                <label
                  key={tagset.id}
                  className="flex items-center gap-3 p-3 rounded-md border cursor-pointer hover:bg-muted/50 transition-colors"
                >
                  <Checkbox
                    checked={selectedTagsetIds.includes(tagset.id)}
                    onCheckedChange={() => toggleTagset(tagset.id)}
                  />
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {sourceIcon(tagset.source_type)}
                    <span className="text-sm font-medium truncate">{tagset.name}</span>
                    <span className="text-xs text-muted-foreground shrink-0">{tagset.tag_count} tags</span>
                  </div>
                </label>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-sm text-muted-foreground">
              No ready tagsets available. <Link to="/app/tagsets/create" className="text-primary hover:underline">Create one</Link> first.
            </div>
          )}

          {selectedTagsetIds.length > 0 && (
            <p className="text-xs text-muted-foreground mt-3">
              {selectedTagsetIds.length} tagset{selectedTagsetIds.length > 1 ? 's' : ''} will be linked after creation.
            </p>
          )}
        </CardContent>
      </Card>

      {/* RAPTOR */}
      <Card className="border-dashed">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-muted-foreground" />
              <CardTitle className="text-base">RAPTOR Enhancement</CardTitle>
            </div>
            <Badge variant="secondary">Coming Soon</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            RAPTOR (Recursive Abstractive Processing for Tree-Organized Retrieval) creates hierarchical summaries
            of your document chunks. This helps answer broad questions that span multiple chunks by providing
            higher-level context that individual chunks cannot capture on their own.
          </p>
        </CardContent>
      </Card>

      {/* Knowledge Graph */}
      <Card className="border-dashed">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Network className="h-5 w-5 text-muted-foreground" />
              <CardTitle className="text-base">Knowledge Graph Extraction</CardTitle>
            </div>
            <Badge variant="secondary">Coming Soon</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Extracts entities (people, organizations, locations, etc.) and their relationships from your documents.
            The resulting knowledge graph enables relationship-aware retrieval, allowing the assistant to answer
            questions about connections between concepts in your data.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
